import { useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import { useSetRecoilState } from "recoil";

import { useUser } from "MVVM/Hooks/useUser";
import { useTrialCardStatus } from "MVVM/Hooks/useTrialCardStatus";
import { StripePaystationState } from "MVVM/State";
import { analytics } from "MVVM/Analytics";
import { getTrialDaysLeft } from "MVVM/Utilities/TrialCountdown";
import { NoticeBanner } from "../NoticeBanner";

/**
 * Countdown for no-card trial users: shown only while a trialing subscription
 * has no payment method attached. Card-on-file trials already auto-convert, so
 * they never see this.
 *
 * Days 7-3: green, dismissal keyed per remaining-day bucket (localStorage), so
 * the banner returns each day the trial gets shorter. Final 2 days: amber
 * urgency variant whose dismissal only lasts the session — it returns every
 * visit until the trial ends or a card is added.
 */
const TrialCountdownBanner = () => {
  const { allstarUser } = useUser();
  const { status } = useTrialCardStatus();
  const theme = useTheme() as any;
  const setStripePaystation = useSetRecoilState(StripePaystationState);

  const daysLeft = getTrialDaysLeft(allstarUser?.user?.userGroup?.expires);
  const urgent = daysLeft <= 2;

  const hash = urgent
    ? "trial-countdown-urgent"
    : `trial-countdown-${daysLeft}d`;
  const dismissKey = `banner-announcement-${hash}-has-been-closed`;
  const [shouldShow, setShouldShow] = useState(true);

  const dismissed = urgent
    ? !!sessionStorage.getItem(dismissKey)
    : !!localStorage.getItem(dismissKey);

  const visible =
    shouldShow &&
    !dismissed &&
    !!allstarUser?.user?.userGroup?.isTrialPeriod &&
    daysLeft > 0 &&
    !!status?.trialing &&
    !status.hasCard;

  useEffect(() => {
    if (visible)
      analytics.track("Trial Countdown Banner - Viewed", { daysLeft });
  }, [visible, daysLeft]);

  if (!visible) return <></>;

  const onDismiss = (show: boolean) => {
    if (!show) {
      analytics.track("Trial Countdown Banner - Dismissed", { daysLeft });
      if (urgent) sessionStorage.setItem(dismissKey, "true");
    }
    setShouldShow(show);
  };

  const onClick = () => {
    analytics.track("Trial Countdown Banner - Add Card Clicked", { daysLeft });
    setStripePaystation({
      isModalOpen: true,
      type: "subscribe",
      pendingEmail: false,
      shouldRefreshMtx: false,
      trialCardSetup: true,
    });
  };

  const dayWord = daysLeft === 1 ? "day" : "days";
  const Wrapper = urgent ? UrgentNotice : NoticeBanner;

  return (
    <Wrapper
      hash={hash}
      backgroundColor={urgent ? theme.colors.caution : theme.colors.envy}
      display="flex"
      setShouldShow={onDismiss}
      onClick={onClick}
      persistDismissal={!urgent}
    >
      {daysLeft === 1 ? (
        <>
          <strong>Last day of Unlimited.</strong> Add a card now to keep it. It
          takes about 30 seconds.
        </>
      ) : urgent ? (
        <>
          <strong>Your trial ends in {daysLeft} days.</strong>{" "}
          {status?.extended
            ? "Add a card to keep Unlimited."
            : "Add a card to keep Unlimited and you'll get 7 bonus trial days."}
        </>
      ) : (
        <>
          {daysLeft} {dayWord} left in your free trial.{" "}
          {status?.extended
            ? "Add a card to keep Unlimited."
            : "Add a card to keep Unlimited and get 7 bonus trial days."}
        </>
      )}
    </Wrapper>
  );
};

// Amber ground needs dark text; NoticeBanner's copy and close icon default to ice
const UrgentNotice = styled(NoticeBanner)`
  div {
    color: ${({ theme }) => theme.colors.midnight};
  }

  svg {
    color: ${({ theme }) => theme.colors.midnight};

    &:hover {
      color: ${({ theme }) => theme.colors.navy};
    }
  }
`;

export { TrialCountdownBanner };
